import { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { Search, MapPin, Plus, Compass, ArrowUpRight, HeartHandshake, Globe2 } from "lucide-react";
import { GLOBAL_COUNTRIES } from "@/lib/data";
import { fileUrl } from "@/lib/files";
import StatusBadge from "@/components/site/StatusBadge";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const ease = [0.22, 1, 0.36, 1];

const STATUS_OPTIONS = [
  ["ACTIVE", "Missing"],
  ["FOUND_SAFE", "Found safe"],
  ["LOCATED", "Located"],
  ["REUNITED", "Reunited"],
  ["CLOSED", "Closed"],
  ["UNKNOWN", "Unknown"],
];

export default function MissingDirectory() {
  const [params, setParams] = useSearchParams();
  const [q, setQ] = useState(params.get("q") || "");
  const [status, setStatus] = useState(params.get("status") || "");
  const [country, setCountry] = useState(params.get("country") || "");
  const [cases, setCases] = useState([]);
  const [total, setTotal] = useState(0);
  const [geo, setGeo] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/v1/missing`, {
        params: { q: q || undefined, status: status || undefined, country: country || undefined },
      });
      setCases(data.cases);
      setTotal(data.total);
    } catch {
      setCases([]);
    } finally {
      setLoading(false);
    }
  }, [q, status, country]);

  useEffect(() => {
    const t = setTimeout(load, 250);
    setParams(Object.fromEntries([...Object.entries({ q, status, country }).filter(([, v]) => v)]));
    return () => clearTimeout(t);
  }, [load]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    axios.get(`${API}/v1/missing/geo-index`).then(({ data }) => setGeo(data.countries || [])).catch(() => {});
  }, []);

  return (
    <main data-testid="missing-directory" className="pt-32 md:pt-40">
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-12">
        <div className="overline text-[color:var(--ink-soft)] mb-6">§ Missing &amp; Found</div>
        <motion.h1
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 1.0, ease }}
          className="font-serif-display leading-[0.95] tracking-[-0.02em]"
          style={{ fontSize: "clamp(44px, 7vw, 120px)" }}
        >
          Help bring them <span className="font-italic-serif italic text-[color:var(--rust)]">home.</span>
        </motion.h1>
        <p className="mt-6 max-w-2xl text-[17px] leading-[1.6] text-[color:var(--ink-soft)]">
          Missing people around the world need to be remembered, searched for and found.
          Every case here is kept by a family — share one, and you may carry the answer.
        </p>
        <div className="mt-8 flex flex-wrap gap-4">
          <Link to="/missing/report" data-testid="missing-report-cta" className="btn-rust">
            <Plus size={15} /> Report a missing person
          </Link>
          <a href="#missing-browse" data-testid="missing-browse-link" className="btn-ghost">
            <Compass size={15} /> Browse by place
          </a>
          <Link to="/map" data-testid="missing-map-link" className="btn-ghost">
            <Globe2 size={15} /> World map
          </Link>
        </div>
      </section>

      {/* Search + filters */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-8">
        <div className="border border-[color:var(--sepia)]/30 bg-[color:var(--parchment)] p-5 md:p-6 flex flex-wrap gap-4 items-center" data-testid="missing-search-bar">
          <label className="flex items-center gap-2 flex-1 min-w-[220px] border-b border-[color:var(--sepia)]/50 pb-2">
            <Search size={15} className="text-[color:var(--sepia)] shrink-0" />
            <input
              data-testid="missing-search-input"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="A name, a case number, a city…"
              className="w-full bg-transparent focus:outline-none placeholder:text-[color:var(--sepia)]/50 text-[16px]"
            />
          </label>
          <select
            data-testid="missing-filter-country"
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none"
          >
            <option value="">Everywhere</option>
            {GLOBAL_COUNTRIES.map((c) => (
              <option key={c.code} value={c.name}>{c.name}</option>
            ))}
            <option value="__other">Other country</option>
          </select>
          <select
            data-testid="missing-filter-status"
            value={status}
            onChange={(e) => setStatus(e.target.value)}
            className="bg-transparent border-b border-[color:var(--sepia)]/50 py-2 text-sm focus:outline-none"
          >
            <option value="">Any status</option>
            {STATUS_OPTIONS.map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="mt-3 numeric-index text-[color:var(--ink-soft)]" data-testid="missing-count">
          {loading ? "Searching…" : `${total} ${total === 1 ? "case" : "cases"} in the ledger`}
        </div>
      </section>

      {/* Case grid */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pb-20">
        {cases.length === 0 && !loading ? (
          <div className="border border-dashed border-[color:var(--ink-line-strong)] p-14 text-center">
            <HeartHandshake size={26} className="mx-auto text-[color:var(--sepia)]" />
            <p className="mt-3 font-italic-serif italic text-xl text-[color:var(--ink-soft)]">
              No cases match — try widening the search.
            </p>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6" data-testid="missing-grid">
            {cases.map((c, i) => (
              <motion.div
                key={c.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: Math.min(i, 8) * 0.04, ease }}
              >
                <Link
                  to={`/missing/case/${c.case_id}`}
                  data-testid={`missing-card-${c.case_id}`}
                  className="group block border border-[color:var(--sepia)]/25 bg-[color:var(--parchment)] hover:shadow-[0_16px_44px_-24px_rgba(43,28,15,0.5)] transition-shadow"
                >
                  <div className="aspect-[4/5] overflow-hidden bg-[color:var(--parchment-2)] flex items-center justify-center">
                    {c.photo_path ? (
                      <img src={fileUrl(c.photo_path)} alt={c.name} className="w-full h-full object-cover group-hover:scale-[1.03] transition-transform duration-700" style={{ filter: "sepia(15%)" }} />
                    ) : (
                      <span className="font-serif-display text-6xl text-[color:var(--sepia)]/40">{c.name?.[0] || "?"}</span>
                    )}
                  </div>
                  <div className="p-4">
                    <div className="flex items-center justify-between gap-2">
                      <StatusBadge status={c.status} />
                      <span className="numeric-index text-[color:var(--ink-soft)]">{c.case_id}</span>
                    </div>
                    <h3 className="mt-2 font-serif-display text-xl leading-snug group-hover:text-[color:var(--rust)] transition-colors">{c.name}</h3>
                    <div className="mt-1 flex items-center gap-1.5 text-xs text-[color:var(--ink-soft)]">
                      <MapPin size={11} />
                      {[c.loc?.city, c.loc?.region, c.loc?.country].filter(Boolean).join(", ")}
                    </div>
                    <div className="mt-1 text-xs text-[color:var(--ink-soft)]">
                      {c.age != null && `Age ${c.age} · `}Last seen {c.last_seen_date}
                    </div>
                  </div>
                </Link>
              </motion.div>
            ))}
          </div>
        )}
      </section>

      {/* Browse by place */}
      {geo.length > 0 && (
        <section id="missing-browse" className="max-w-[1600px] mx-auto px-6 md:px-10 pb-24" data-testid="missing-geo-index">
          <div className="rule-strong mb-10" />
          <div className="overline text-[color:var(--ink-soft)] mb-6 flex items-center gap-2">
            <Compass size={13} /> Browse by place
          </div>
          <div className="flex flex-wrap gap-3">
            {geo.map((g) => (
              <button
                key={g.country}
                onClick={() => setCountry(g.country)}
                data-testid={`missing-geo-${(g.country_code || g.country).toLowerCase()}`}
                className="border border-[color:var(--sepia)]/40 px-4 py-2 hover:bg-[color:var(--parchment-2)] transition-colors flex items-center gap-2"
              >
                <span className="font-serif-display">{g.country}</span>
                <span className="numeric-index text-[color:var(--ink-soft)]">{g.active} missing</span>
                <ArrowUpRight size={12} className="text-[color:var(--sepia)]/60" />
              </button>
            ))}
          </div>
        </section>
      )}
    </main>
  );
}
